"use client";

import { flushSync } from "react-dom";

import "./circle-view-transition.css";

/** Applied to `document.documentElement` for the duration of a circular reveal VT. */
export const CIRCULAR_REVEAL_TRANSITION_CLASS = "circular-reveal-transition";

/**
 * Clears `data-stable-vt-surface` names for this VT so learn/topic shells join
 * `::view-transition-*(root)`. Theme toggle omits this so learn stays instant.
 */
export const CIRCULAR_REVEAL_INCLUDE_STABLE_CLASS =
	"circular-reveal-include-stable";

export interface RunCircularRevealViewTransitionOptions {
	/**
	 * When true, stable learn surfaces participate in the root circle (completion
	 * overlay open/close). Default false preserves theme-toggle behavior.
	 */
	includeStableSurfaces?: boolean;
}

function setCircularRevealOriginFromElement(
	originElement?: Element | null,
): void {
	if (typeof window === "undefined") {
		return;
	}
	if (!originElement || !(originElement instanceof HTMLElement)) {
		document.documentElement.style.removeProperty("--x");
		document.documentElement.style.removeProperty("--y");
		return;
	}
	const rect = originElement.getBoundingClientRect();
	const x = (rect.left + rect.right) / 2;
	const y = (rect.top + rect.bottom) / 2;
	document.documentElement.style.setProperty(
		"--x",
		`${(x / window.innerWidth) * 100}%`,
	);
	document.documentElement.style.setProperty(
		"--y",
		`${(y / window.innerHeight) * 100}%`,
	);
}

function nextFrame(): Promise<void> {
	return new Promise((resolve) => {
		requestAnimationFrame(() => resolve());
	});
}

/**
 * Runs `document.startViewTransition` with a circular reveal on **`::view-transition-new(root)`**.
 * Sets `--x` / `--y` on `document.documentElement` from `originElement` (viewport %), or defaults to 50% / 50%.
 *
 * **Sync update only** — an async VT callback (e.g. double-rAF inside the update)
 * made Chrome skip the circle (instant cut). Preload / paint-settle **before**
 * calling this (see completion open + battle preloader), then `flushSync` here.
 */
export async function runCircularRevealViewTransition(
	update: () => void,
	originElement?: Element | null,
	options?: RunCircularRevealViewTransitionOptions,
): Promise<void> {
	if (typeof document === "undefined" || typeof window === "undefined") {
		update();
		return;
	}

	if (typeof document.startViewTransition !== "function") {
		flushSync(update);
		return;
	}

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		flushSync(update);
		return;
	}

	setCircularRevealOriginFromElement(originElement);

	const includeStable = options?.includeStableSurfaces === true;
	document.documentElement.classList.add(CIRCULAR_REVEAL_TRANSITION_CLASS);
	if (includeStable) {
		document.documentElement.classList.add(
			CIRCULAR_REVEAL_INCLUDE_STABLE_CLASS,
		);
	}

	try {
		const transition = document.startViewTransition(() => {
			flushSync(update);
		});
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				try {
					transition.skipTransition();
				} catch {
					// ignore — already finished / unsupported
				}
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		try {
			await transition.finished;
		} finally {
			document.removeEventListener(
				"visibilitychange",
				onVisibilityChange,
			);
		}
	} finally {
		document.documentElement.classList.remove(
			CIRCULAR_REVEAL_TRANSITION_CLASS,
		);
		document.documentElement.classList.remove(
			CIRCULAR_REVEAL_INCLUDE_STABLE_CLASS,
		);
	}
}

/** Double-rAF so layout/paint can settle before starting a circular VT. */
export async function waitForNextPaint(): Promise<void> {
	if (typeof window === "undefined") {
		return;
	}
	await nextFrame();
	await nextFrame();
}
